import * as React from 'react'

import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'

import moment from 'moment'
import { connect } from 'react-redux'
import { TidewaysContext } from '../../app/context'
import { Entity } from '../../app/entity'
import { EventGraphData, MarkerEvent, ReleaseEvent, TidewaysEvent } from '../../domain/Event'
import { SnapshotComparison } from '../../domain/SnapshotComparison'
import Loading from '../../helper/loading'
import { Number } from '../../helper/number'
import { ApplicationState } from '../../redux/ApplicationState'
import Tooltip from '../graph/tooltip'
import ChangedTransactionsList from './changedTransactionsList'
import SnapshotComparisonGraph from './snapshotComparisonGraph'
import NotificationLog from '../../errors/details/NotificationLog'
import { ActionExecution } from '../../domain/ActionExecution'
import { Box, BoxBody } from '../../ui/box'

interface State {
    activeSlot: {
        timeIndex: any
        mouse: any
    }
}

interface StateProps {
    loading: boolean
    events: TidewaysEvent[]
    snapshotComparison: SnapshotComparison
}

type Props = StateProps & {
    title: string
    router: any
    event: MarkerEvent | ReleaseEvent
    relatedAlertActionExecutions?: ActionExecution[]
    tidewaysContext: TidewaysContext
    showDetails: boolean
}

class PerformanceComparisonWithTransactions extends React.Component<Props, State> {
    public state: State = {
        activeSlot: {
            timeIndex: null,
            mouse: null,
        },
    }

    public render() {
        if (this.props.loading || !this.props.snapshotComparison) {
            return <Loading />
        }

        const snapshotComparison = this.props.snapshotComparison
        const improved = snapshotComparison.improvedTransactions
        const degraded = snapshotComparison.degradedTransactions
        const serviceComparison = snapshotComparison.serviceComparison
        const reportMerged = snapshotComparison.reportMerged
        const summaryBefore = snapshotComparison.summaryBefore
        const summaryAfter = snapshotComparison.summaryAfter
        const { relatedAlertActionExecutions } = this.props
        const comparisonReference = this.props.tidewaysContext.application.license.traceAggregation
            ? { type: 'event', referenceId: this.props.event.id.toString() }
            : null

        return (
            <>
                <div className="box event-box jquery-tooltip-snapshot-comparison-graph">
                    <Tooltip
                        data={{ report: reportMerged }}
                        activeSlot={this.state.activeSlot}
                        percentileLabel="95% Percentile"
                        downstreamEnabled={reportMerged.hasDownstream}
                        showAverages={false}
                        parentSelector=".jquery-tooltip-snapshot-comparison-graph"
                        moveUp={20}
                    />
                    <div className="box-header">
                        <nav className="navbar navbar-toolbar">
                            <h3 className="box-title">
                                {this.props.title}: {this.props.event.name}
                            </h3>
                        </nav>
                    </div>
                    <div className="box-body">
                        {this.props.event.description ? <p>Description: {this.props.event.description}</p> : null}
                        {!this.props.showDetails || !reportMerged ? null : (
                            <div className="row snapshotComparison">
                                <div className={reportMerged.slices.total ? 'col-lg-10' : 'col-lg-8'}>
                                    <SnapshotComparisonGraph
                                        router={this.props.router}
                                        reportMerged={reportMerged}
                                        events={this.props.events}
                                        changeActiveSlot={this.changeActiveSlot}
                                    />
                                </div>

                                {reportMerged.slices.total ? (
                                    <div className="col-lg-2 centered">
                                        <p className="numbers">
                                            <big>
                                                <Milliseconds value={reportMerged.slices.total.responseTime * 1000} />
                                            </big>
                                            <br />
                                            <small>Response Time</small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Number value={reportMerged.slices.total.requests} />
                                            </big>
                                            <br />
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={reportMerged.slices.total.errorRate / 100} />
                                                </big>
                                                <br />
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}

                                {summaryBefore ? (
                                    <div className="col-lg-2 centered">
                                        <p>
                                            <big>Before</big>
                                            <br />
                                            <small className="text-muted">
                                                {moment
                                                    .utc(summaryBefore.range.startDate)
                                                    .local()
                                                    .format('HH:mm')}{' '}
                                                -{' '}
                                                {moment
                                                    .utc(summaryBefore.range.endDate)
                                                    .local()
                                                    .format('HH:mm')}
                                            </small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Milliseconds value={summaryBefore.percentileResponse * 1000} />
                                            </big>
                                            <br />
                                            <small>Response Time</small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Number value={summaryBefore.requests} />
                                            </big>
                                            <br />
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={summaryBefore.errorRate / 100} />
                                                </big>
                                                <br />
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}

                                {summaryAfter ? (
                                    <div className="col-lg-2 centered">
                                        <p>
                                            <big>After</big>
                                            <br />
                                            <small className="text-muted">
                                                {moment
                                                    .utc(summaryAfter.range.startDate)
                                                    .local()
                                                    .format('HH:mm')}{' '}
                                                -{' '}
                                                {moment
                                                    .utc(summaryAfter.range.endDate)
                                                    .local()
                                                    .format('HH:mm')}
                                            </small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Milliseconds value={summaryAfter.percentileResponse * 1000} />
                                            </big>
                                            {serviceComparison ? (
                                                <small
                                                    style={
                                                        serviceComparison.responseTimeChange * 100 < -5
                                                            ? { color: '#00a65a' }
                                                            : serviceComparison.responseTimeChange * 100 > 5
                                                            ? { color: '#f56954' }
                                                            : { color: '#344ec9' }
                                                    }
                                                >
                                                    {serviceComparison.responseTimeChange > 0 ? ' +' : ' '}
                                                    <Percent value={serviceComparison.responseTimeChange} />
                                                    <br />
                                                </small>
                                            ) : (
                                                <br />
                                            )}
                                            <small>Response Time</small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Number value={summaryAfter.requests} />
                                            </big>
                                            {serviceComparison ? (
                                                <small
                                                    style={
                                                        serviceComparison.requestsChange * 100 < -10 ||
                                                        serviceComparison.requestsChange * 100 > 10
                                                            ? { color: '#f56954' }
                                                            : serviceComparison.requestsChange * 100 > 5 ||
                                                              serviceComparison.requestsChange * 100 < -5
                                                            ? { color: '#00a65a' }
                                                            : { color: '#344ec9' }
                                                    }
                                                >
                                                    {serviceComparison.requestsChange > 0 ? ' +' : ' '}
                                                    <Percent value={serviceComparison.requestsChange} />
                                                    <br />
                                                </small>
                                            ) : (
                                                <br />
                                            )}
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={summaryAfter.errorRate / 100} />
                                                </big>
                                                {serviceComparison ? (
                                                    <small
                                                        style={
                                                            serviceComparison.errorsChange < 0
                                                                ? { color: '#00a65a' }
                                                                : serviceComparison.errorsChange > 5
                                                                ? { color: '#f56954' }
                                                                : { color: '#344ec9' }
                                                        }
                                                    >
                                                        {serviceComparison.errorsChange > 0 ? ' +' : ' '}
                                                        <Percent value={serviceComparison.errorsChange} />
                                                        <br />
                                                    </small>
                                                ) : (
                                                    <br />
                                                )}
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
                {!this.props.showDetails || !snapshotComparison ? null : (
                    <div className="row">
                        <div className="col-sm-12">
                            <ul className="nav nav-pills nav-pills-tideways">
                                <li className="active">
                                    <a href="#transactions" data-target="#transactions" data-toggle="tab">
                                        Transactions
                                    </a>
                                </li>
                                <li>
                                    <a href="#notificationLog" data-target="#notificationLog" data-toggle="tab">
                                        Notification Log
                                    </a>
                                </li>
                            </ul>

                            <Box>
                                <BoxBody noPadding>
                                    <div className="tab-content no-padding">
                                        <div className="tab-pane active" id="transactions">
                                            <ChangedTransactionsList
                                                transactions={degraded}
                                                type={'degraded'}
                                                isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                                comparisonReference={comparisonReference}
                                            />
                                            <ChangedTransactionsList
                                                transactions={improved}
                                                type={'improved'}
                                                isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                                comparisonReference={comparisonReference}
                                            />
                                        </div>
                                        <div className="tab-pane" id="notificationLog">
                                            <NotificationLog actionExecutions={relatedAlertActionExecutions} />
                                        </div>
                                    </div>
                                </BoxBody>
                            </Box>
                        </div>
                    </div>
                )}
            </>
        )
    }

    private changeActiveSlot = activeSlot => this.setState({ activeSlot })
}

export default connect((globalState: ApplicationState) => {
    const routeParameters = globalState.tidewaysContext.route.parameters

    // Depending on which route we are using the currently selected event id might be given as "event" or "error"
    const key = routeParameters.event || routeParameters.error

    const eventGraph = globalState.events.eventGraphs[key] || (new Entity() as Entity<EventGraphData>)

    return {
        loading: eventGraph.loading,
        snapshotComparison: eventGraph.data?.snapshotComparison,
        events: eventGraph.data?.events,
        ...eventGraph.data,
    }
})(PerformanceComparisonWithTransactions)
